import cx from "clsx";
import {
  GalleryArtwork,
  SecondaryOfferEntry,
} from "kreate-common/modules/business-types";
import ButtonLink from "kreate-common/modules/kreate-ui/components/ButtonLink";
import ButtonOutline from "kreate-common/modules/kreate-ui/components/ButtonOutline";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import { parseKreateUtxoOffer } from "kreate-common/modules/utxo-parser/by-type/offer";
import { CLIENT_ENV } from "kreate-env/client";
import React from "react";
import { MdEdit } from "react-icons/md";

import SmallGalleryArtworkViewer from "../../../../components/SmallGalleryArtworkViewer";
import Decorators from "../../components/Decorators";
import Definition from "../ModalListAsset/components/Definition";

import styles from "./index.module.scss";
import { getProfitBreakdown } from "./utils";

import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import {
  formatInteger$Compact,
  formatInteger$Standard,
  formatLovelaceAmount$Compact,
  formatLovelaceAmount$Standard,
} from "@/modules/common-utils/format";
import { InlineError } from "@/modules/form-field/errors";
import { fromNatural, toNatural } from "@/modules/form-field/utils/conversions";
import Format from "@/modules/kreate-components/components/Format";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";
import { useModalTrigger$ModalPrompt } from "@/modules/kreate-modals/modals/ModalPrompt/hooks";

type SubmitEvent = {
  numEditions: number;
};

export type ModalAcceptOffer$SubmitEvent = SubmitEvent;

type Props = {
  open: boolean;
  galleryArtwork: GalleryArtwork;
  numEditionsInWallet: number;
  offer: SecondaryOfferEntry;
  onSubmit?: (event: SubmitEvent) => void | Promise<void>;
  onCancel?: () => void;
};

export default function ModalAcceptOffer({
  open,
  galleryArtwork,
  numEditionsInWallet,
  offer,
  onSubmit,
  onCancel,
}: Props) {
  const modalTriggers = {
    ModalFailure: useModalTrigger$ModalFailure(),
    ModalPrompt: useModalTrigger$ModalPrompt(),
  };

  const { busy, withErrorHandler } = useWithErrorHandler(
    async (error: unknown) =>
      await modalTriggers.ModalFailure.openModalWithError(error)
  );

  const { datumOffer } = parseKreateUtxoOffer(offer.utxo);
  const royaltyRate = datumOffer.editionRoyalty / datumOffer.editionPrice;

  const minNumEditions = 1;

  const maxNumEditions = Math.min(
    numEditionsInWallet,
    datumOffer.wantedQuantity,
    1e6
  );

  const [numEditions, setNumEditions] = React.useState(
    Math.max(minNumEditions, maxNumEditions)
  );

  const handleSubmit = withErrorHandler(async () => {
    await onSubmit?.({ numEditions });
  });

  const profitBreakdown = getProfitBreakdown({ datumOffer, numEditions });

  const handleClickUpdateNumEditions = withErrorHandler(async () => {
    const draft = await modalTriggers.ModalPrompt.openModal("No. Editions", {
      initialValue: fromNatural(numEditions),
      validate: (draft) => {
        const value = toNatural(draft);
        InlineError.assert$PunishAfterEditing(
          value >= minNumEditions,
          `Cannot be less than ${minNumEditions}`
        );
        InlineError.assert$PunishAfterEditing(
          value <= maxNumEditions,
          `Cannot be more than ${maxNumEditions}`
        );
      },
      modalProps: { size: "sm" },
    });
    setNumEditions(toNatural(draft));
  });

  return (
    <ModalV2 className={styles.container} open={open} onClose={onCancel}>
      <ModalV2.HeaderV2
        title="Viewing Offer Details"
        description={
          <div>
            <span>{"This Offer was made by "}</span>
            <strong>
              <Format.Address address={datumOffer.offererAddress} unstyled />
            </strong>
            <span>{"."}</span>
          </div>
        }
      />
      <ModalV2.Content>
        <Flex.Col gap="24px">
          <Decorators.HorizontalDivider />
          <Flex.Row gap="32px">
            <Flex.Cell minWidth="0" flex="0 1 auto" asChild>
              <SmallGalleryArtworkViewer galleryArtwork={galleryArtwork} />
            </Flex.Cell>
            <Decorators.VerticalDivider />
            <Flex.Cell flex="0 0 auto" asChild>
              <Definition
                label="Price"
                content={formatLovelaceAmount$Compact(datumOffer.editionPrice)}
                tooltip={formatLovelaceAmount$Standard(datumOffer.editionPrice)}
              />
            </Flex.Cell>
            <Decorators.VerticalDivider />
            <Flex.Cell flex="0 0 auto" asChild>
              <Definition
                label="Quantity"
                content={formatInteger$Compact(datumOffer.wantedQuantity)}
                tooltip={formatInteger$Standard(datumOffer.wantedQuantity)}
              />
            </Flex.Cell>
          </Flex.Row>
          <Decorators.HorizontalDivider />
          <div className={styles.grid}>
            <Flex.Row className={styles.name} gap="8px" alignItems="baseline">
              <span>{"Artwork Price"}</span>
              <ButtonLink
                alwaysUnderline
                onClick={handleClickUpdateNumEditions}
                disabled={minNumEditions === maxNumEditions}
              >
                <Flex.Row
                  className={styles.fontWeight600}
                  gap="4px"
                  alignItems="center"
                >
                  <Format.Integer
                    integer={numEditions}
                    formatContent={(x) => `${x} Editions`}
                    unstyled
                  />
                  <MdEdit />
                </Flex.Row>
              </ButtonLink>
            </Flex.Row>
            <div className={styles.value}>
              <Format.LovelaceAmount
                lovelaceAmount={profitBreakdown?.artworkPrice}
                unstyled
              />
            </div>
            <div className={styles.name}>
              <Format.Percentage
                ratio={royaltyRate}
                formatContent={(x) => `Royalty (${x})`}
                unstyled
              />
            </div>
            <div className={styles.value}>
              <Format.LovelaceAmount
                lovelaceAmount={profitBreakdown?.artistReceives}
                unstyled
              />
            </div>
            <div className={styles.name}>
              <Format.Percentage
                ratio={CLIENT_ENV.KOLLECTOR_MINT_FEE_PERCENTAGE / 100}
                formatContent={(x) => `Platform Fee (${x})`}
                unstyled
              />
            </div>
            <div className={styles.value}>
              <Format.LovelaceAmount
                lovelaceAmount={profitBreakdown?.protocolReceives}
                unstyled
              />
            </div>
            <div className={styles.name}>{"Transaction Fee"}</div>
            <div className={styles.value}>
              <Format.LovelaceAmount
                lovelaceAmount={profitBreakdown?.transactionFeeEstimated}
                approx
                unstyled
              />
            </div>
            <div className={styles.divider}></div>
            <div className={cx(styles.name, styles.total)}>
              {"Your earnings"}
            </div>
            <div className={cx(styles.value, styles.total)}>
              <Format.LovelaceAmount
                lovelaceAmount={profitBreakdown?.sellerReceivesEstimated}
                approx
                unstyled
              />
            </div>
          </div>
        </Flex.Col>
      </ModalV2.Content>
      <ModalV2.Actions>
        <ButtonOutline
          size="lg"
          content="Cancel"
          onClick={onCancel}
          disabled={busy}
        />
        <ButtonPrimary
          size="lg"
          content="Submit"
          onClick={handleSubmit}
          disabled={busy}
        />
      </ModalV2.Actions>
    </ModalV2>
  );
}
